import { useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { findCurrentAccount } from '../api/endpoints/reports';
import type { CurrentAccount } from '../api/types/reports';

function optionLabel(option: CurrentAccount): string {
  const title = option.title1 || option.title2 || '';
  return option.currentCode ? `${title} (${option.currentCode})` : title;
}

/** Cari hesap (running/corporate account) autocomplete backed by
 *  `Report/FindCurrentAccount` (min 3 chars, debounced via staleTime) — same
 *  search-as-you-type pattern as `AirportAutocomplete`. `EmployeeId` is hardcoded to 0
 *  for now (see api/types/reports.ts header); will be wired up once the corporate
 *  module can supply the logged-in employee's id. */
export function CurrentAccountAutocomplete({
  label = 'Cari Hesap',
  value,
  onChange,
}: {
  label?: string;
  value: CurrentAccount | null;
  onChange: (value: CurrentAccount | null) => void;
}) {
  const [inputValue, setInputValue] = useState('');
  const trimmed = inputValue.trim();

  const { data: options = [], isFetching } = useQuery({
    queryKey: ['report', 'currentAccountQuery', trimmed],
    queryFn: () =>
      findCurrentAccount({ employeeId: 0, key: trimmed }).then(
        (res) => res.currentAccountList ?? (res.currentAccounts ? [res.currentAccounts] : [])
      ),
    enabled: trimmed.length >= 3,
    staleTime: 60_000,
  });

  return (
    <Autocomplete
      sx={{ minWidth: 240, flex: 1 }}
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      options={options}
      filterOptions={(x) => x}
      getOptionLabel={optionLabel}
      isOptionEqualToValue={(option, val) => option.currentCode === val.currentCode}
      loading={isFetching}
      noOptionsText={trimmed.length < 3 ? 'Aramak için en az 3 karakter girin' : 'Cari hesap bulunamadı'}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <>
                  {isFetching && <CircularProgress color="inherit" size={16} />}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}
